import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import { GridColDef } from "@mui/x-data-grid-pro";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { I18nGrid } from "@/components/data/I18nGrid";
import OutgoingUrlCell from "@/components/grid/cells/OutgoingUrlCell";
import useMaintenanceColumns from "@/hooks/columns/useMaintenanceColumns";
import { useMaintenanceModalRows } from "@/hooks/maintenance/useMaintenanceModalRows";
import { useUpdateMaintenanceJob } from "@/hooks/maintenance/useUpdateMaintenanceWorkOrderJob";
import { DisplayMaintenance, MaintenanceDisplayData } from "@/models/MaintenanceDisplayData";

export interface MaintenanceModalProps {
  open: boolean;
  handleClose: () => void;
  modalData: DisplayMaintenance;
}

export default function MaintenanceModal(props: MaintenanceModalProps) {
  const { t } = useTranslation();
  const { open, handleClose, modalData } = props;

  const { initialColumns } = useMaintenanceColumns(true);
  const updateJob = useUpdateMaintenanceJob();
  const rows = useMaintenanceModalRows(modalData);

  const columns = React.useMemo<GridColDef[]>(
    () => [
      ...initialColumns,
      {
        field: "outgoingUrl",
        headerName: "Link",
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        width: 50,
        renderCell: OutgoingUrlCell,
      },
    ],
    [initialColumns],
  );
  if (!modalData) return null;

  const processRowUpdate = async (
    newRow: MaintenanceDisplayData,
    oldRow: MaintenanceDisplayData,
  ) => {
    if (newRow.statusCode !== oldRow.statusCode) {
      updateJob.mutate({
        job: newRow.workOrderJob,
        newStatus: newRow.statusCode,
      });
    }
    // MUI X requires the row to be returned even on a no-op, or it commits `undefined`
    // into the grid model and throws on the next edit.
    return newRow;
  };
  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 2,
          p: 3,
          width: "80%",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography variant="h6" gutterBottom>
          {t("maintenanceModal.titleRequest")} / {t("maintenanceModal.titleJob")}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {rows.length === 0 ? (
          <Typography sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
            {t("common.noData") ?? "No maintenance data"}
          </Typography>
        ) : (
          <Box sx={{ flex: 1, minHeight: 360 }}>
            <I18nGrid
              rows={rows}
              columns={columns}
              disableRowSelectionOnClick
              density="compact"
              pageSizeOptions={[5, 10, 25]}
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
                sorting: { sortModel: [{ field: "expirationDate", sort: "asc" }] },
              }}
              processRowUpdate={processRowUpdate}
              sx={{
                border: 0,
                "& .MuiDataGrid-cell": { alignItems: "center" },
                "& .MuiDataGrid-columnHeaders": { borderBottom: "none" },
              }}
            />
          </Box>
        )}

        <Box mt={2} textAlign="right">
          <Button variant="contained" onClick={handleClose}>
            {t("maintenanceModal.close")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
